import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Menu, Card, Statistic, Row, Col, Typography, Avatar,
  Dropdown, Button, Space, Input, Progress, Alert,
  Tag, Descriptions, Tooltip, Empty
} from 'antd';
import { Link } from 'react-router-dom';
import {
  MenuFoldOutlined, MenuUnfoldOutlined, SearchOutlined,
  UserOutlined, LogoutOutlined, HomeOutlined,
  FileTextOutlined, TrophyOutlined, BarChartOutlined,
  TeamOutlined, SolutionOutlined, RocketOutlined, CheckCircleTwoTone,
  WarningTwoTone, StarFilled
} from '@ant-design/icons';
import { Line, Pie } from '@ant-design/charts';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const BRAND  = { primary: '#4B32C3', bg: '#F5F7FB' };
const RADIUS = 12;
const SHADOW = '0 3px 12px rgba(0,0,0,.05)';

function useCollege() {
  try {
    const raw = localStorage.getItem('auth');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function CollegeDashboard() {
  const college  = useCollege();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  if (!college) {
    return (
      <Empty
        description="Авторизуйтесь, чтобы просмотреть дэшборд"
        style={{ marginTop: 120 }}
      >
        <Button type="primary" onClick={() => navigate('/')}>На страницу входа</Button>
      </Empty>
    );
  }

  const loginEmail = college.currentLogin || college.username || college.college_email;

  const KPIS = [
    { title: 'Контингент',    value: +college.contingent, icon: <TeamOutlined /> },
    { title: 'Преподаватели', value: +college.teachers,   icon: <SolutionOutlined /> },
    { title: 'Мощность',      value: +college.power,      suffix: ' мест', icon: <RocketOutlined /> },
    { title: 'Общежитие',     value: college.hostel === 'да' ? 'Есть' : 'Нет', icon: <HomeOutlined /> },
  ];

  const specs = college.spec_code_names?.split(';').filter(Boolean);
  const quals = college.qualif?.split(';').filter(Boolean);

  const lineData = Array.from({ length: 5 }).map((_, i) => ({
    year: 2018 + i,
    rank: Math.round(10 - i - Math.random() * 3),
  }));
  const pieData  = specs?.slice(0, 5).map(s => ({
    type: (s.split('–')[1] || s).trim(),
    value: Math.round(Math.random() * 20) + 5,
  }));
  const lineCfg = {
    data: lineData, xField: 'year', yField: 'rank', height: 250, smooth: true,
    yAxis: { min: 0, max: 10 }, lineStyle: { stroke: BRAND.primary, lineWidth: 3 },
    point: { size: 4, style: { fill: BRAND.primary } },
  };
  const pieCfg = {
    data: pieData, angleField: 'value', colorField: 'type', innerRadius: 0.55,
    height: 250, legend: { position: 'right' }, label: false,
  };

  const profileMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: 'Профиль колледжа' },
      { type: 'divider' },
      { key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Выйти',
        onClick: () => { localStorage.clear(); navigate('/'); },
      },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={220}
        theme="light"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}
      >
        <div style={{
          height: 56, display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start', paddingInline: 24,
        }}>
          {collapsed ? (
   <svg width="25" height="51" viewBox="0 0 45 51" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.77487 16.6822L0 12.1542V39.3224L7.77487 43.3738V34.3177L22.8534 42.4206V33.3645L7.77487 25.2617V16.6822Z" fill="#113F71"/>
<path d="M15.0785 20.257L8.48168 16.2056L22.8534 8.1028L15.7853 4.0514L22.8534 0L33.9267 6.0771L45 12.1542L37.9319 16.2056L30.1571 12.1542L15.0785 20.257Z" fill="#113F71"/>
<path d="M37.4607 16.6822L30.1571 20.257V38.8458L23.3246 42.4206V51L45 39.3224V34.9135V30.5047L41.466 32.4112L37.9319 34.3177L37.4607 16.6822Z" fill="#113F71"/>
</svg>

  ) : (
  <svg width="143" height="51" viewBox="0 0 223 51" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.77487 16.6822L0 12.1542V39.3224L7.77487 43.3738V34.3177L22.8534 42.4206V33.3645L7.77487 25.2617V16.6822Z" fill="#113F71"/>
<path d="M15.0785 20.257L8.48168 16.2056L22.8534 8.1028L15.7853 4.0514L22.8534 0L33.9267 6.0771L45 12.1542L37.9319 16.2056L30.1571 12.1542L15.0785 20.257Z" fill="#113F71"/>
<path d="M37.4607 16.6822L30.1571 20.257V38.8458L23.3246 42.4206V51L45 39.3224V34.9135V30.5047L41.466 32.4112L37.9319 34.3177L37.4607 16.6822Z" fill="#113F71"/>
<path d="M77.4734 43H72.0291V12.9583H62V8H87.5025V12.9583H77.4734V43Z" fill="#113F71"/>
<path d="M104.695 8L90.3679 43H96.9585L106.701 18.5L116.157 43H121.888L108.134 8H104.695Z" fill="#113F71"/>
<path d="M152.548 43H130.484V8H135.929V38.3333H152.548V43Z" fill="#113F71"/>
<path d="M173.466 8L159.425 43H165.443L175.185 18.5L185.214 43H190.945L177.191 8H173.466Z" fill="#113F71"/>
<path d="M196.963 8V43H202.407V34.25H211.576C213.869 34.25 215.535 33.571 217.021 32.5011C228.89 23.9525 220.97 8 211.576 8H196.963Z" fill="#113F71"/>
<path d="M202 29V14H210.91C220.808 16.2233 217.035 29 210.91 29H202Z" fill="white"/>
</svg>

  )}
        </div>

        <Menu
  mode="inline"
  defaultSelectedKeys={['dashboard']}
  items={[
    {
      key: 'dashboard',
      icon: <HomeOutlined />,
      label: <Link to="/dashboard">Кабинет</Link>,
    },
    {
      key: 'opros',
      icon: <FileTextOutlined />,
      label: <Link to="/opros">Опросы</Link>,
    },
    {
      key: 'manager',
      icon: <TrophyOutlined />,
      label: <Link to="/manager">Рейтинги (Проверка)</Link>,
    },
    {
      key: 'profile',
      icon: <BarChartOutlined />,
      label: <Link to="/profile">Профиль</Link>,
    },
  ]}
/>
      </Sider>

      <Layout>
        <Header style={{
          background: '#fff', paddingInline: 24, display: 'flex',
          alignItems: 'center', gap: 16, borderBottom: '1px solid #f0f0f0',
        }}>
          <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed(!collapsed)} />

          <Title level={5} style={{ margin: 0 }}>{college.college_name.trim()}</Title>

          <Input prefix={<SearchOutlined />} placeholder="Поиск…" allowClear style={{ maxWidth: 260, marginLeft: 'auto' }} />
          <Dropdown menu={profileMenu} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar style={{ background: BRAND.primary }}>
                {college.college_name.match(/\b\p{L}/u)?.[0] || 'C'}
              </Avatar>
              {!collapsed && (
                <div style={{ lineHeight: 1 }}>
                  <Text strong style={{ fontSize: 12 }}>{loginEmail}</Text><br/>
                  <Text type="secondary" style={{ fontSize: 11 }}>{college.city}</Text>
                </div>
              )}
            </Space>
          </Dropdown>
        </Header>

        <Content style={{ background: BRAND.bg, padding: '32px 48px' }}>
          <Row gutter={[24, 24]}>
            {college.jas_maman === 'да' && (
              <Col span={24}>
                <Alert
                  type="success"
                  message="Участник программы «Жас маман»"
                  description="Колледж модернизирован в рамках госпрограммы."
                  showIcon
                  icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                  style={{ borderRadius: RADIUS }}
                />
              </Col>
            )}
            {college.area_type === 'сельская местность' && (
              <Col span={24}>
                <Alert
                  type="warning"
                  message="Сельская локация"
                  description="Колледж находится в сельской местности."
                  showIcon
                  icon={<WarningTwoTone twoToneColor="#faad14" />}
                  style={{ borderRadius: RADIUS }}
                />
              </Col>
            )}
          </Row>

          <Card bordered={false} style={{ borderRadius: RADIUS, boxShadow: SHADOW, marginTop: 24 }}>
            <Alert
              type="info"
              showIcon
              message={`Вы вошли как: ${loginEmail}`}
              style={{ borderRadius: 8, marginBottom: 24 }}
            />

            <Descriptions title="Основная информация" size="small" column={{ xs: 1, md: 2, xl: 3 }}>
              <Descriptions.Item label="Регион">{college.region}</Descriptions.Item>
              <Descriptions.Item label="Район">{college.area}</Descriptions.Item>
              <Descriptions.Item label="Нас. пункт">{college.city}</Descriptions.Item>
              <Descriptions.Item label="Адрес">{college.adress}</Descriptions.Item>
              <Descriptions.Item label="Директор">{college.name.trim()}</Descriptions.Item>
              <Descriptions.Item label="Телефон">{college.tel}</Descriptions.Item>
              <Descriptions.Item label="Сайт">
                <a href={`https://${college.site}`} target="_blank" rel="noreferrer">{college.site}</a>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            {KPIS.map(k => (
              <Col xs={24} sm={12} xl={6} key={k.title}>
                <Card bordered={false} style={{ borderRadius: RADIUS, boxShadow: SHADOW }} bodyStyle={{ padding: 24 }}>
                  <Row justify="space-between" align="middle">
                    <Col><Statistic title={k.title} value={k.value} suffix={k.suffix} /></Col>
                    <Col><Avatar size={40} icon={k.icon} style={{ background: '#F0F5FF', color: BRAND.primary }} /></Col>
                  </Row>
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
            <Col xs={24} lg={12}>
              <Card title="Динамика рейтинга" bordered={false} style={{ borderRadius: RADIUS, boxShadow: SHADOW }}>
                <Line {...lineCfg} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Распределение по направлениям" bordered={false} style={{ borderRadius: RADIUS, boxShadow: SHADOW }}>
                <Pie {...pieCfg} />
              </Card>
            </Col>
          </Row>
          {specs?.length > 0 && (
            <Card bordered={false} style={{ borderRadius: RADIUS, boxShadow: SHADOW, marginTop: 32 }}>
              <Title level={5}>Специальности</Title>
              {specs.map((s, i) => (
                <Tag key={i} icon={<StarFilled />} color="blue" style={{ marginBottom: 8 }}>{s.trim()}</Tag>
              ))}
            </Card>
          )}

          {quals?.length > 0 && (
            <Card bordered={false} style={{ borderRadius: RADIUS, boxShadow: SHADOW, marginTop: 24 }}>
              <Title level={5}>Квалификации</Title>
              {quals.map((q, i) => (
                <Tag key={i} color="purple" style={{ marginBottom: 8 }}>{q.trim()}</Tag>
              ))}
            </Card>
          )}

    
          <Card bordered={false} style={{ borderRadius: RADIUS, boxShadow: SHADOW, marginTop: 32 }}>
            <Row align="middle" gutter={24}>
              <Col flex="none">
                <Tooltip title="Опрос «Качество образования 2023»">
                  <Avatar shape="square" size={48} style={{ background: BRAND.primary }}>
                    <FileTextOutlined />
                  </Avatar>
                </Tooltip>
              </Col>
              <Col flex="auto">
                <Text strong>Опрос «Рейтинг колледжей 2025» завершается 30 августа</Text>
                <Progress percent={75} status="active" style={{ marginTop: 8 }} />
              </Col>
              <Col flex="none">
                <Button type="primary">Ответить</Button>
              </Col>
            </Row>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
}
